import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { MetaGamesEventType, type MetagamesCurrentEvent, type HammerAnimationState } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { BUCKET_PREFIX } from '../../utils/environment';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class HammersStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    this.loadHammerFrames();

    const hammersFlag = resolveFlag('hammers_enabled', true);
    this.setHammersFlag(hammersFlag);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('hammers_enabled' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.resolveAndUpdateFlags();
      } else {
        this.setHammersFlag(false);
      }
    });
  }

  private resolveAndUpdateFlags() {
    const hammersEnabledFF = resolveFlag('hammers_enabled', true);
    this.setHammersFlag(hammersEnabledFF);
  }

  @observable
  public hammersFlag = false;

  @action
  public setHammersFlag(flag: boolean) {
    this.hammersFlag = flag;
  }

  @observable
  public frameLoaded = false;

  @action
  public setFrameLoaded(loaded: boolean) {
    this.frameLoaded = loaded;
  }

  @observable
  public hammerAnimationState: HammerAnimationState = {
    hammerAmount: 0,
    enable: false,
  };

  @action
  setHammerAnimationState(state: Partial<HammerAnimationState>) {
    this.hammerAnimationState = { ...this.hammerAnimationState, ...state };
  }

  public claimHammersFrames: HTMLImageElement[] = [];

  public loadHammerFrames() {
    try {
      if (this.claimHammersFrames.length) return;
      const frameCount = 31;
      const framesPath = `${BUCKET_PREFIX}png/hammers-seq/HAMMER_win_Widget_V2__`;

      for (let i = 0; i <= frameCount; i++) {
        const img = new Image();
        img.src = `${framesPath}${i.toString().padStart(5, '0')}.png`;

        img.onload = () => {
          this.claimHammersFrames[i] = img;
          if (this.claimHammersFrames.length === frameCount) {
            this.setFrameLoaded(true);
          }
        };
      }
    } catch (error) {
      devError('Error loading hammer frames', error);
    }
  }

  public startClaimHammersAnimation({
    hammerAmount,
    onAnimationEnd,
  }: {
    hammerAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
  }) {
    const featureFlag = resolveFlag('claim_hammer_animation_enable', false);
    if (!this.frameLoaded || !featureFlag) {
      void onAnimationEnd?.();
      return;
    }
    this.setHammerAnimationState({
      hammerAmount,
      enable: true,
      onAnimationEnd: () => {
        this.setHammerAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }

  @computed
  public get hammersEventByCoin() {
    if (!this.hammersEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.hammersEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @computed
  public get currentOpenHammersEventByCoin() {
    if (!this.currentOpenHammersEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.currentOpenHammersEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public hammersEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setHammersEvent(event: MetagamesCurrentEvent | undefined) {
    this.hammersEvent = event;
  }

  @observable
  public currentOpenHammersEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenHammersEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenHammersEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  private updateEventOptedInStatus(eventId: string, coinType: CoinType, isOptedIn: boolean) {
    if (!this.hammersEvent) {
      devError('HammersStore: updateEventOptedInStatus error: hammersEvent not found');
      return;
    }

    const coinTypeLower = coinType.toLowerCase() as 'gc' | 'sc';

    if (this.hammersEvent && this.hammersEvent.id === eventId && this.hammersEvent[coinTypeLower]) {
      const updatedCurrentEvent = {
        ...this.hammersEvent,
        [coinTypeLower]: {
          ...this.hammersEvent[coinTypeLower],
          isOptedIn,
        },
      };

      this.setHammersEvent(updatedCurrentEvent);
    }
  }

  public async acknowledgeOptIn(eventId: string) {
    try {
      const { coinType } = this.rootStore.coinTypeStore;

      this.updateEventOptedInStatus(eventId, coinType, true);

      await this.rootStore.metagamesStore.optInToEvent(eventId, coinType);
    } catch (error) {
      devError('HammersStore: acknowledgeOptIn error', error);
    }
  }

  public startDialogs(type?: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (this.hammersEvent && type && ['Hammers', 'WelcomeHummersMetagame'].includes(type)) {
      if (this.hammersEventByCoin && !this.hammersEventByCoin.isOptedIn) {
        void this.startHammersGame();
        return true;
      }
    }

    return false;
  }

  public async startHammersGame() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.hammersEvent) {
        devError('HammersStore: startHammersGame error: hammersEvent not found');
        return;
      }

      const eventByCoin = this.hammersEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('HammersStore: startHammersGame error: event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('HammersStore: startHammersGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (metagames) {
        const { token, path } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          metagamesToken: gameUiFlag ? token : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.hammersEvent,
          token,
          path,
          url: metagamesUrl,
        };

        this.setCurrentOpenHammersEvent(metagamesEventToSet);

        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenMetagamesDialog,
          options: { type: MetaGamesEventType.HAMMERS },
        });
      } else {
        devError('HammersStore: startHammersGame error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('HammersStore: startHammersGame error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame() {
    this.setCurrentOpenHammersEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.featureManagerStore.onDialogFinished('Metagames');
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();
  }

  public handleHammersIframeLoad(iframeRef: HTMLIFrameElement) {
    if (!iframeRef.contentWindow) {
      devError('HammersStore handleHammersIframeLoad: Iframe content window is not available');
      return;
    }

    this.rootStore.metagamesStore.setIframeRef(iframeRef);

    if (this.currentOpenHammersEvent) {
      iframeRef.contentWindow.postMessage(
        {
          type: 'newMetagamesEvent',
          payload: {
            metagamesEvent: toJS(this.currentOpenHammersEvent),
          },
        },
        import.meta.env.VITE_GAME_UI_URL,
      );
    }
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: 'hammers',
    });
  }

  public resetHammers() {
    this.setIsLoadingGame(false);
    this.setHammersEvent(undefined);
    this.setCurrentOpenHammersEvent(undefined);
  }
}
